import type { PublicKey, EncryptedBlob } from '@shared/types';
import {
  base64ToBytes,
  base64UrlToBytes,
  bytesToBase64,
  toArrayBuffer,
  utf8ToBytes,
  bytesToUtf8
} from '../utils/encoding';
import { ed25519, x25519 } from '@noble/curves/ed25519.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { createEd25519PrivateJwk } from './ed25519Provider';

export interface SigningKeyPair {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
  privateKeyJwk: JsonWebKey;
}

export interface EncryptionKeyPair {
  publicKey: Uint8Array;
  secretKey: Uint8Array;
}

/**
 * Generate master key (AES-256 compatible)
 */
export async function generateMasterKey(): Promise<CryptoKey> {
  const key = await crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true, // extractable
    ['encrypt', 'decrypt']
  );

  return key;
}

/**
 * Generate Ed25519 key pair for signing
 */
export async function generateSigningKeyPair(): Promise<SigningKeyPair> {
  const subtle = globalThis.crypto?.subtle;
  if (!subtle) {
    const secretKey = crypto.getRandomValues(new Uint8Array(32));
    const publicKey = ed25519.getPublicKey(secretKey);
    return {
      publicKey,
      secretKey,
      privateKeyJwk: createEd25519PrivateJwk(secretKey, publicKey)
    };
  }

  let keyPair: CryptoKeyPair;
  try {
    keyPair = await subtle.generateKey(
      { name: 'Ed25519' },
      true,
      ['sign', 'verify']
    ) as CryptoKeyPair;
  } catch {
    const secretKey = crypto.getRandomValues(new Uint8Array(32));
    const publicKey = ed25519.getPublicKey(secretKey);
    return {
      publicKey,
      secretKey,
      privateKeyJwk: createEd25519PrivateJwk(secretKey, publicKey)
    };
  }
  const publicKey = new Uint8Array(await subtle.exportKey('raw', keyPair.publicKey));
  const privateKeyJwk = await subtle.exportKey('jwk', keyPair.privateKey);
  if (!privateKeyJwk.d) {
    throw new Error('Generated Ed25519 JWK is missing private component.');
  }

  const secretKey = base64UrlToBytes(privateKeyJwk.d);
  return {
    publicKey,
    secretKey,
    privateKeyJwk: {
      ...privateKeyJwk,
      key_ops: ['sign'],
      ext: true
    }
  };
}

/**
 * Generate an independent X25519 key pair for encryption/ECDH.
 * The secret key is a raw 32-byte scalar and must never be used for Ed25519 signing.
 */
export function generateEncryptionKeyPair(): EncryptionKeyPair {
  const secretKey = crypto.getRandomValues(new Uint8Array(32));
  const publicKey = x25519.getPublicKey(secretKey);
  return { publicKey, secretKey };
}

export async function importMasterKeyFromRaw(rawBytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    toArrayBuffer(rawBytes),
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

export async function exportMasterKeyRaw(masterKey: CryptoKey): Promise<Uint8Array> {
  const raw = await crypto.subtle.exportKey('raw', masterKey);
  return new Uint8Array(raw);
}

/**
 * Encrypt data with AES-GCM
 */
export async function encryptWithMasterKey(
  masterKey: CryptoKey,
  data: string
): Promise<EncryptedBlob> {
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const dataBytes = utf8ToBytes(data);

  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: toArrayBuffer(nonce)
    },
    masterKey,
    toArrayBuffer(dataBytes)
  );

  return {
    cipher: 'aes-256-gcm',
    data: bytesToBase64(new Uint8Array(encrypted)),
    nonce: bytesToBase64(nonce),
    version: 1
  };
}

/**
 * Decrypt data with AES-GCM
 */
export async function decryptWithMasterKey(
  masterKey: CryptoKey,
  blob: EncryptedBlob
): Promise<string> {
  const dataBytes = base64ToBytes(blob.data);
  const nonceBytes = base64ToBytes(blob.nonce);

  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: toArrayBuffer(nonceBytes)
    },
    masterKey,
    toArrayBuffer(dataBytes)
  );

  return bytesToUtf8(new Uint8Array(decrypted));
}

/**
 * Derive a deterministic AES-256-GCM vault encryption key from identity private key bytes.
 * All devices sharing the same identity key will derive the same vault key.
 */
export async function deriveVaultKey(identityPrivateKeyBytes: Uint8Array): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(identityPrivateKeyBytes),
    'HKDF',
    false,
    ['deriveKey']
  );
  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode('vault-v2')
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Create PublicKey object from Ed25519 public key
 */
export function createPublicKey(publicKeyBytes: Uint8Array, algorithm: 'ed25519' | 'x25519'): PublicKey {
  return {
    algorithm,
    value: bytesToBase64(publicKeyBytes)
  };
}

/**
 * Compute fingerprint from public key
 */
export async function computeFingerprint(publicKey: PublicKey): Promise<string> {
  const publicKeyBytes = base64ToBytes(publicKey.value);
  const hash = await crypto.subtle.digest('SHA-256', toArrayBuffer(publicKeyBytes));
  const hashBytes = new Uint8Array(hash);

  return Array.from(hashBytes)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Format fingerprint for display
 */
export function formatFingerprint(fingerprint: string): string {
  return fingerprint.match(/.{1,4}/g)?.join(' ') || fingerprint;
}

/**
 * Export master key encrypted with password
 */
export async function exportMasterKeyWithPassword(
  masterKey: CryptoKey,
  password: string
): Promise<EncryptedBlob> {
  // Derive key from password using PBKDF2
  const passwordBytes = utf8ToBytes(password);
  const salt = crypto.getRandomValues(new Uint8Array(16));

  const baseKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(passwordBytes),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: toArrayBuffer(salt),
      iterations: 600000,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt']
  );

  // Export master key
  const masterKeyBytes = await crypto.subtle.exportKey('raw', masterKey);

  // Encrypt master key with derived key
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv: toArrayBuffer(nonce)
    },
    derivedKey,
    masterKeyBytes
  );

  // Prepend salt to encrypted data
  const result = new Uint8Array(salt.length + encrypted.byteLength);
  result.set(salt);
  result.set(new Uint8Array(encrypted), salt.length);

  return {
    cipher: 'aes-256-gcm',
    data: bytesToBase64(result),
    nonce: bytesToBase64(nonce),
    version: 1
  };
}

/**
 * Import master key decrypted with password
 */
export async function importMasterKeyWithPassword(
  blob: EncryptedBlob,
  password: string
): Promise<CryptoKey> {
  const dataWithSalt = base64ToBytes(blob.data);
  const salt = dataWithSalt.slice(0, 16);
  const encryptedData = dataWithSalt.slice(16);
  const nonce = base64ToBytes(blob.nonce);

  // Derive key from password
  const passwordBytes = utf8ToBytes(password);
  const baseKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(passwordBytes),
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: toArrayBuffer(salt),
      iterations: 600000,
      hash: 'SHA-256'
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt']
  );

  // Decrypt master key
  const decrypted = await crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: toArrayBuffer(nonce)
    },
    derivedKey,
    toArrayBuffer(encryptedData)
  );

  // Import decrypted master key
  return crypto.subtle.importKey(
    'raw',
    decrypted,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

const MK_TRANSFER_HKDF_INFO = new TextEncoder().encode('mk-transfer-v2');

/**
 * Encrypt master key for a target device using ECIES over X25519.
 *
 * Scheme:
 *   ephemeralPriv  = random X25519 scalar
 *   ephemeralPub   = x25519(ephemeralPriv, G)
 *   shared         = x25519(ephemeralPriv, recipientPub)
 *   key            = HKDF-SHA256(ikm=shared, salt=ephemeralPub, info='mk-transfer-v2', 32 bytes)
 *   ciphertext     = AES-GCM-256(key, masterKeyBytes)
 *   data           = base64(ephemeralPub[32] || ciphertext)
 */
export async function exportMasterKeyForDevice(
  masterKey: CryptoKey,
  recipientX25519PubKey: Uint8Array
): Promise<EncryptedBlob> {
  const ephemeralPriv = crypto.getRandomValues(new Uint8Array(32));
  const ephemeralPub = x25519.getPublicKey(ephemeralPriv);
  const shared = x25519.getSharedSecret(ephemeralPriv, recipientX25519PubKey);
  const keyBytes = hkdf(sha256, shared, ephemeralPub, MK_TRANSFER_HKDF_INFO, 32);

  const aesKey = await crypto.subtle.importKey('raw', toArrayBuffer(keyBytes), { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
  const masterKeyBytes = await crypto.subtle.exportKey('raw', masterKey);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce }, aesKey, masterKeyBytes));

  const data = new Uint8Array(ephemeralPub.length + ciphertext.length);
  data.set(ephemeralPub);
  data.set(ciphertext, ephemeralPub.length);

  return { cipher: 'aes-256-gcm', data: bytesToBase64(data), nonce: bytesToBase64(nonce), version: 2 };
}

/**
 * Decrypt master key using own X25519 scalar (ECIES counterpart to exportMasterKeyForDevice).
 */
export async function importMasterKeyForDevice(
  blob: EncryptedBlob,
  recipientEcdhScalar: Uint8Array
): Promise<CryptoKey> {
  const dataBytes = base64ToBytes(blob.data);
  const ephemeralPub = dataBytes.slice(0, 32);
  const ciphertext = dataBytes.slice(32);
  const nonce = base64ToBytes(blob.nonce);

  const shared = x25519.getSharedSecret(recipientEcdhScalar, ephemeralPub);
  const keyBytes = hkdf(sha256, shared, ephemeralPub, MK_TRANSFER_HKDF_INFO, 32);

  const aesKey = await crypto.subtle.importKey('raw', toArrayBuffer(keyBytes), { name: 'AES-GCM', length: 256 }, false, ['decrypt']);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: toArrayBuffer(nonce) }, aesKey, toArrayBuffer(ciphertext));

  return crypto.subtle.importKey('raw', decrypted, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}
